import type {
  PublicResolvedSlot,
  PublicStoreWebsiteDto,
  PublicWebsiteBlock,
  PublicWebsiteBlockDataSource,
  WebsiteComponentId,
  WebsiteMediaFraming,
  WebsiteResolvedTheme,
} from '../../types/website-templates';
import {
  WEBSITE_COLOR_TOKENS,
  WEBSITE_EMPTY_BEHAVIORS,
  WEBSITE_INTERFACE_LOCALES,
  WEBSITE_LAYOUT_FAMILIES,
  WEBSITE_MEDIA_KINDS,
  WEBSITE_MERCHANDISING_KEYS,
} from '../../types/website-templates';
import { buildImageProxyUrl, buildStorefrontMediaProxyUrl } from '../image-proxy';
import {
  WEBSITE_COMPONENT_SURFACE,
  WEBSITE_RENDERER_MAJOR,
  isSupportedWebsiteVariant,
} from './capabilities';
import { isWebsiteHexColor } from './contrast';
import { isAllowedSocialUrl } from './external-url';
import { isWebsiteFontId } from './fonts';
import {
  WEBSITE_BLOCK_ID_RE,
  WEBSITE_COLLECTION_REF_RE,
  WEBSITE_SLOT_ID_MAX_LENGTH,
  WEBSITE_SLOT_ID_RE,
  WEBSITE_TEMPLATE_ASSET_KEY_RE,
  WEBSITE_UUID_RE,
  isOneOf,
  isRecord,
  type UnknownRecord,
} from './internal';
import { validateWebsiteMediaFraming } from './media-frames';
import { WEBSITE_MEDIA_ALT_MAX_LENGTH } from './media-policy';
import { isWebsitePageKind } from './page-kinds';
import { WEBSITE_MEDIA_REF_PREFIX, WEBSITE_TEMPLATE_ASSET_REF_PREFIX } from './resolve';
import { sanitizeStorefrontNewsletterCollectionConfig } from '../storefront-engine/content';
import { validateWebsiteRichText } from './slots';
import { WEBSITE_TEXT_MAX_LENGTH, checkMerchantText } from './text-safety';

export const WEBSITE_TEMPLATE_ASSETS_BUCKET = 'website-template-assets';

/**
 * `unsupported` = well-formed but needs newer renderer code (A7: show the
 * neutral "store updating" shell). `invalid` = malformed; fail closed.
 */
export type PublicStoreWebsiteParse =
  | { readonly ok: true; readonly dto: PublicStoreWebsiteDto }
  | { readonly ok: false; readonly reason: 'unsupported' | 'invalid' };

class UnsupportedError extends Error {}
class InvalidError extends Error {}

function exact(
  value: UnknownRecord,
  required: readonly string[],
  optional: readonly string[] = [],
) {
  const keys = Object.keys(value);
  if (
    !required.every((key) => keys.includes(key)) ||
    !keys.every((key) => required.includes(key) || optional.includes(key))
  ) {
    throw new InvalidError();
  }
}

function isMediaRef(value: unknown): value is string {
  if (typeof value !== 'string') return false;
  if (value.startsWith(WEBSITE_MEDIA_REF_PREFIX)) {
    return WEBSITE_UUID_RE.test(value.slice(WEBSITE_MEDIA_REF_PREFIX.length));
  }
  if (value.startsWith(WEBSITE_TEMPLATE_ASSET_REF_PREFIX)) {
    return WEBSITE_TEMPLATE_ASSET_KEY_RE.test(
      value.slice(WEBSITE_TEMPLATE_ASSET_REF_PREFIX.length),
    );
  }
  return false;
}

const SAFE_PATH_HREF_RE = /^\/s\/[a-z0-9]+(?:-[a-z0-9]+)*(?:\/[A-Za-z0-9_-]+)*$/;
const SAFE_TEL_HREF_RE = /^tel:\+[1-9][0-9]{7,14}$/;
const SAFE_WHATSAPP_HREF_RE = /^https:\/\/wa\.me\/[1-9][0-9]{7,14}$/;

/**
 * Store-relative paths, store contact links and, since epic #228 P5, the
 * allowlisted Instagram/Facebook URLs (`isAllowedSocialUrl`, the same rule
 * the server stores and emits). Never any other free URL; at most 256 chars.
 */
export function isSafeWebsiteHref(value: unknown): value is string {
  return (
    typeof value === 'string' &&
    value.length <= 256 &&
    (SAFE_PATH_HREF_RE.test(value) ||
      SAFE_TEL_HREF_RE.test(value) ||
      SAFE_WHATSAPP_HREF_RE.test(value) ||
      isAllowedSocialUrl(value))
  );
}

function parseSlot(value: unknown): PublicResolvedSlot {
  if (!isRecord(value)) throw new InvalidError();
  switch (value.type) {
    case 'newsletter-collection': {
      exact(value, ['type', 'collection']);
      if (!isRecord(value.collection)) throw new InvalidError();
      exact(value.collection, ['enabled', 'contactMethods', 'consentVersion']);
      const collection = sanitizeStorefrontNewsletterCollectionConfig(value.collection);
      if (!collection) throw new InvalidError();
      return { type: 'newsletter-collection', collection };
    }
    case 'text': {
      exact(value, ['type', 'text']);
      const text = checkMerchantText(value.text, {
        maxLength: WEBSITE_TEXT_MAX_LENGTH,
        multiline: true,
      });
      if (!text.ok) throw new InvalidError();
      return { type: 'text', text: value.text as string };
    }
    case 'rich-text': {
      exact(value, ['type', 'doc']);
      const doc = validateWebsiteRichText(value.doc, WEBSITE_TEXT_MAX_LENGTH);
      if (!doc.ok) throw new InvalidError();
      return { type: 'rich-text', doc: doc.doc };
    }
    case 'link':
      exact(value, ['type', 'href']);
      if (!isSafeWebsiteHref(value.href)) throw new InvalidError();
      return { type: 'link', href: value.href };
    case 'media': {
      // Epic #228 P6: `framing` is the one optional key (exact otherwise).
      exact(value, ['type', 'kind', 'src', 'posterSrc', 'alt', 'autoplay'], ['framing']);
      const hasFraming = Object.prototype.hasOwnProperty.call(value, 'framing');
      if (hasFraming && validateWebsiteMediaFraming(value.framing) !== null) {
        throw new InvalidError();
      }
      if (!isOneOf(WEBSITE_MEDIA_KINDS, value.kind) || !isMediaRef(value.src)) {
        throw new InvalidError();
      }
      if (value.posterSrc !== null && !isMediaRef(value.posterSrc)) throw new InvalidError();
      if (value.kind !== 'image' && value.posterSrc === null) throw new InvalidError();
      if (typeof value.autoplay !== 'boolean') throw new InvalidError();
      if (value.autoplay && value.kind !== 'video') throw new InvalidError();
      const alt = checkMerchantText(value.alt, {
        maxLength: WEBSITE_MEDIA_ALT_MAX_LENGTH,
        allowEmpty: true,
      });
      if (!alt.ok) throw new InvalidError();
      return {
        type: 'media',
        kind: value.kind,
        src: value.src,
        posterSrc: value.posterSrc,
        alt: value.alt as string,
        autoplay: value.autoplay,
        ...(hasFraming ? { framing: value.framing as WebsiteMediaFraming } : {}),
      };
    }
    case 'category-ref':
    case 'product-ref':
      exact(value, ['type', 'ref']);
      if (typeof value.ref !== 'string' || !WEBSITE_UUID_RE.test(value.ref)) {
        throw new InvalidError();
      }
      return { type: value.type, ref: value.ref };
    case 'custom-collection-ref':
      exact(value, ['type', 'ref']);
      if (typeof value.ref !== 'string' || !WEBSITE_COLLECTION_REF_RE.test(value.ref)) {
        throw new InvalidError();
      }
      return { type: 'custom-collection-ref', ref: value.ref };
    default:
      throw new UnsupportedError();
  }
}

function parseDataSource(value: unknown): PublicWebsiteBlockDataSource {
  if (!isRecord(value)) throw new InvalidError();
  if (value.kind === 'merchandising') {
    exact(value, ['kind', 'key', 'limit']);
    if (!isOneOf(WEBSITE_MERCHANDISING_KEYS, value.key)) throw new UnsupportedError();
    if (!Number.isInteger(value.limit) || (value.limit as number) < 1) throw new InvalidError();
    if ((value.limit as number) > 24) throw new InvalidError();
    return { kind: 'merchandising', key: value.key, limit: value.limit as number };
  }
  if (value.kind === 'public-categories') {
    exact(value, ['kind', 'limit']);
    if (!Number.isInteger(value.limit) || (value.limit as number) < 1) throw new InvalidError();
    if ((value.limit as number) > 12) throw new InvalidError();
    return { kind: 'public-categories', limit: value.limit as number };
  }
  const categoryId = (id: unknown): string | null => {
    if (id === null) return null;
    if (typeof id !== 'string' || !WEBSITE_UUID_RE.test(id)) throw new InvalidError();
    return id;
  };
  if (value.kind === 'category-products') {
    exact(value, ['kind', 'categoryOrdinal', 'categoryId', 'limit']);
    if (
      ![1, 2, 3].includes(value.categoryOrdinal as number) ||
      !Number.isInteger(value.limit) ||
      (value.limit as number) < 1 ||
      (value.limit as number) > 8
    )
      throw new InvalidError();
    return {
      kind: 'category-products',
      categoryOrdinal: value.categoryOrdinal as 1 | 2 | 3,
      categoryId: categoryId(value.categoryId),
      limit: value.limit as number,
    };
  }
  if (value.kind === 'bound-categories') {
    exact(value, ['kind', 'bindings', 'limit']);
    if (
      !Number.isInteger(value.limit) ||
      (value.limit as number) < 1 ||
      (value.limit as number) > 6 ||
      !Array.isArray(value.bindings) ||
      value.bindings.length !== value.limit
    )
      throw new InvalidError();
    const bindings = value.bindings.map((binding: unknown, index: number) => {
      if (!isRecord(binding)) throw new InvalidError();
      exact(binding, ['ordinal', 'categoryId']);
      if (binding.ordinal !== index + 1) throw new InvalidError();
      return { ordinal: index + 1, categoryId: categoryId(binding.categoryId) };
    });
    const ids = bindings.flatMap((binding) =>
      binding.categoryId === null ? [] : [binding.categoryId],
    );
    if (new Set(ids).size !== ids.length) throw new InvalidError();
    return { kind: 'bound-categories', bindings, limit: value.limit as number };
  }
  throw new UnsupportedError();
}

function parseBlocks(
  value: unknown,
  layoutFamily: (typeof WEBSITE_LAYOUT_FAMILIES)[number],
  surface: string,
): PublicWebsiteBlock[] {
  if (!Array.isArray(value) || value.length > 24) throw new InvalidError();
  if (value.filter((raw) => isRecord(raw) && raw.component === 'chrome.newsletter').length > 1)
    throw new InvalidError();
  const blocks = value.map((raw) => {
    if (!isRecord(raw)) throw new InvalidError();
    exact(raw, ['id', 'component', 'variant', 'slots', 'emptyBehavior'], ['dataSource']);
    if (typeof raw.id !== 'string' || !WEBSITE_BLOCK_ID_RE.test(raw.id)) throw new InvalidError();
    if (typeof raw.component !== 'string' || typeof raw.variant !== 'string') {
      throw new InvalidError();
    }
    if (!Object.prototype.hasOwnProperty.call(WEBSITE_COMPONENT_SURFACE, raw.component)) {
      throw new UnsupportedError();
    }
    const component = raw.component as WebsiteComponentId;
    if (WEBSITE_COMPONENT_SURFACE[component] !== surface) throw new InvalidError();
    if (!isSupportedWebsiteVariant(layoutFamily, component, raw.variant)) {
      throw new UnsupportedError();
    }
    if (!isOneOf(WEBSITE_EMPTY_BEHAVIORS, raw.emptyBehavior)) throw new UnsupportedError();
    if (!isRecord(raw.slots) || Object.keys(raw.slots).length > 60) throw new InvalidError();

    const slots: Record<string, PublicResolvedSlot> = {};
    for (const [slotId, slot] of Object.entries(raw.slots)) {
      if (slotId.length > WEBSITE_SLOT_ID_MAX_LENGTH || !WEBSITE_SLOT_ID_RE.test(slotId)) {
        throw new InvalidError();
      }
      const parsed = parseSlot(slot);
      if ((slotId === 'chrome.newsletter.collection') !== (parsed.type === 'newsletter-collection'))
        throw new InvalidError();
      if (
        parsed.type === 'newsletter-collection' &&
        (component !== 'chrome.newsletter' ||
          raw.id !== 'newsletter' ||
          slotId !== 'chrome.newsletter.collection')
      )
        throw new InvalidError();
      slots[slotId] = parsed;
    }
    const dataSource = raw.dataSource === undefined ? undefined : parseDataSource(raw.dataSource);
    const campaign = layoutFamily === 'editorial-fashion' && component === 'home.categoryCampaign';
    const boundTiles =
      (layoutFamily === 'editorial-fashion' || layoutFamily === 'luxury-jewelry') &&
      component === 'home.categoryTiles';
    if (
      (campaign && dataSource?.kind !== 'category-products') ||
      (boundTiles && dataSource?.kind !== 'bound-categories') ||
      (dataSource?.kind === 'category-products' && !campaign) ||
      (dataSource?.kind === 'bound-categories' && !boundTiles) ||
      (component === 'home.newsletter' && dataSource !== undefined)
    )
      throw new InvalidError();
    return {
      id: raw.id,
      component,
      variant: raw.variant,
      slots,
      emptyBehavior: raw.emptyBehavior,
      ...(dataSource === undefined ? {} : { dataSource }),
    };
  });
  const ordinals = blocks.flatMap((block) =>
    block.dataSource?.kind === 'category-products' ? [block.dataSource.categoryOrdinal] : [],
  );
  if (
    new Set(ordinals).size !== ordinals.length ||
    blocks.filter((block) => block.component === 'home.newsletter').length > 1
  )
    throw new InvalidError();
  return blocks;
}

function parseTheme(value: unknown): WebsiteResolvedTheme {
  if (!isRecord(value)) throw new InvalidError();
  exact(value, ['colors', 'fonts']);
  if (!isRecord(value.colors) || !isRecord(value.fonts)) throw new InvalidError();
  exact(value.colors, WEBSITE_COLOR_TOKENS);
  exact(value.fonts, ['heading', 'body']);
  const colors = {} as Record<(typeof WEBSITE_COLOR_TOKENS)[number], string>;
  for (const token of WEBSITE_COLOR_TOKENS) {
    const color = value.colors[token];
    if (!isWebsiteHexColor(color)) throw new InvalidError();
    colors[token] = color.toLowerCase();
  }
  if (!isWebsiteFontId(value.fonts.heading) || !isWebsiteFontId(value.fonts.body)) {
    throw new UnsupportedError();
  }
  return { colors, fonts: { heading: value.fonts.heading, body: value.fonts.body } };
}

/**
 * Strict parser for the public wire DTO. Exact keys at every level, so a
 * response carrying schema, ids, checksums or audit fields is rejected.
 */
export function parsePublicStoreWebsiteDto(value: unknown): PublicStoreWebsiteParse {
  try {
    if (!isRecord(value)) throw new InvalidError();
    if (value.mode === 'legacy') {
      exact(value, ['mode', 'interfaceLocale']);
      if (!isOneOf(WEBSITE_INTERFACE_LOCALES, value.interfaceLocale)) throw new UnsupportedError();
      return { ok: true, dto: { mode: 'legacy', interfaceLocale: value.interfaceLocale } };
    }
    if (value.mode !== 'template') throw new UnsupportedError();
    exact(value, ['mode', 'site', 'page'], ['legacyEngine']);

    const site = value.site;
    const page = value.page;
    if (!isRecord(site) || !isRecord(page)) throw new InvalidError();
    exact(site, [
      'layoutFamily',
      'rendererMajor',
      'interfaceLocale',
      'direction',
      'theme',
      'chrome',
    ]);
    exact(page, ['kind', 'blocks']);

    if (!isOneOf(WEBSITE_LAYOUT_FAMILIES, site.layoutFamily)) throw new UnsupportedError();
    if (site.rendererMajor !== WEBSITE_RENDERER_MAJOR) throw new UnsupportedError();
    if (!isOneOf(WEBSITE_INTERFACE_LOCALES, site.interfaceLocale)) throw new UnsupportedError();
    const direction = site.interfaceLocale === 'en' ? 'ltr' : 'rtl';
    if (site.direction !== direction) throw new InvalidError();
    if (!isWebsitePageKind(page.kind)) throw new UnsupportedError();

    const layoutFamily = site.layoutFamily;
    let legacyEngine: PublicStoreWebsiteDto['legacyEngine'];
    if (value.legacyEngine !== undefined) {
      if (layoutFamily !== 'legacy-engine-v2' || !isRecord(value.legacyEngine)) {
        throw new InvalidError();
      }
      exact(value.legacyEngine, ['engineVersion', 'config']);
      const { engineVersion, config } = value.legacyEngine;
      if ((engineVersion !== 1 && engineVersion !== 2) || !isRecord(config)) {
        throw new InvalidError();
      }
      legacyEngine = { engineVersion, config };
    } else if (layoutFamily === 'legacy-engine-v2') {
      // A frozen design without its page cannot be drawn.
      throw new InvalidError();
    }

    return {
      ok: true,
      dto: {
        mode: 'template',
        site: {
          layoutFamily,
          rendererMajor: WEBSITE_RENDERER_MAJOR,
          interfaceLocale: site.interfaceLocale,
          direction,
          theme: parseTheme(site.theme),
          chrome: parseBlocks(site.chrome, layoutFamily, 'chrome'),
        },
        page: { kind: page.kind, blocks: parseBlocks(page.blocks, layoutFamily, page.kind) },
        ...(legacyEngine ? { legacyEngine } : {}),
      },
    };
  } catch (error) {
    return { ok: false, reason: error instanceof UnsupportedError ? 'unsupported' : 'invalid' };
  }
}

/** Opaque media reference → fetchable URL. Storage paths never leave SQL. */
export function websiteMediaUrl(reference: string, supabaseUrl: string): string | null {
  if (reference.startsWith(WEBSITE_MEDIA_REF_PREFIX)) {
    const id = reference.slice(WEBSITE_MEDIA_REF_PREFIX.length);
    return WEBSITE_UUID_RE.test(id) ? buildStorefrontMediaProxyUrl(supabaseUrl, id) : null;
  }
  if (reference.startsWith(WEBSITE_TEMPLATE_ASSET_REF_PREFIX)) {
    const key = reference.slice(WEBSITE_TEMPLATE_ASSET_REF_PREFIX.length);
    return WEBSITE_TEMPLATE_ASSET_KEY_RE.test(key)
      ? buildImageProxyUrl(supabaseUrl, WEBSITE_TEMPLATE_ASSETS_BUCKET, key)
      : null;
  }
  return null;
}

function hydrateBlocks(
  blocks: readonly PublicWebsiteBlock[],
  supabaseUrl: string,
): PublicWebsiteBlock[] {
  return blocks.map((block) => {
    const slots: Record<string, PublicResolvedSlot> = {};
    for (const [slotId, slot] of Object.entries(block.slots)) {
      if (slot.type !== 'media') {
        slots[slotId] = slot;
        continue;
      }
      const src = websiteMediaUrl(slot.src, supabaseUrl);
      if (!src) continue;
      const posterSrc = slot.posterSrc ? websiteMediaUrl(slot.posterSrc, supabaseUrl) : null;
      slots[slotId] = { ...slot, src, posterSrc };
    }
    return { ...block, slots };
  });
}

/** Replaces opaque media references with proxy URLs for rendering. */
export function hydratePublicStoreWebsiteDto(
  dto: PublicStoreWebsiteDto,
  supabaseUrl: string,
): PublicStoreWebsiteDto {
  if (dto.mode !== 'template' || !dto.site || !dto.page) return dto;
  return {
    ...dto,
    site: { ...dto.site, chrome: hydrateBlocks(dto.site.chrome, supabaseUrl) },
    page: { ...dto.page, blocks: hydrateBlocks(dto.page.blocks, supabaseUrl) },
  };
}
